import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getBackendUrl } from "@/lib/backend";
import { useScope } from "@/context/ScopeContext";
import "./UsmRiskSchoolsModal.css";

const API = `${getBackendUrl()}/api`;
const PAGE_SIZE = 10;

const fmt = (v) => (typeof v === "number" ? v.toLocaleString("en-IN") : v ?? "—");
const pct = (v, digits = 1) => (typeof v === "number" ? `${v.toFixed(digits)}%` : "—");

export const DATA_ENTRY_KPI_MODAL_CONFIG = {
  completion_rate: {
    id: "completion_rate",
    label: "Completion %",
    fallbackTitle: "Completion Rate — Schools Below 100%",
  },
  pending_students: {
    id: "pending_students",
    label: "Pending students",
    fallbackTitle: "Pending Students — Schools With Open Records",
  },
  non_certified: {
    id: "non_certified",
    label: "Certified",
    fallbackTitle: "Non-Certified — Schools Awaiting Officer Certification",
  },
  critical_schools: {
    id: "critical_schools",
    label: "Completion %",
    fallbackTitle: "Critical Schools — Below 95% Completion",
  },
  high_repeaters: {
    id: "high_repeaters",
    label: "Repeater rate",
    fallbackTitle: "High Repeater Schools — Above 5% Repeater Rate",
  },
  certified_schools: {
    id: "certified_schools",
    label: "Certified",
    fallbackTitle: "Certified Schools — Officer Validated",
  },
  repeaters: {
    id: "repeaters",
    label: "Repeaters",
    fallbackTitle: "Repeater Schools — Students Flagged for Review",
  },
};

const formatCoverageSummary = (metric, districtPct, districtAggregate, data) => {
  if (metric === "pending_students") {
    const pending =
      typeof districtAggregate === "number"
        ? districtAggregate
        : typeof data?.district_aggregate === "number"
          ? data.district_aggregate
          : null;
    if (pending != null) {
      return (
        <>
          <b>{fmt(pending)}</b> pending students · {pct(districtPct ?? data?.district_pct, 2)} of enrolment
        </>
      );
    }
  }
  if (metric === "repeaters") {
    const repeaters =
      typeof districtAggregate === "number"
        ? districtAggregate
        : typeof data?.district_aggregate === "number"
          ? data.district_aggregate
          : null;
    if (repeaters != null) {
      return (
        <>
          <b>{fmt(repeaters)}</b> repeater records · {pct(districtPct ?? data?.district_pct, 2)} of enrolment
        </>
      );
    }
  }
  if (metric === "non_certified" || metric === "certified_schools") {
    const rate = districtPct ?? data?.district_pct;
    return rate != null ? <>Certification rate <b>{pct(rate, 1)}</b></> : "—";
  }
  if (metric === "high_repeaters") {
    const rate = districtPct ?? data?.district_pct;
    return rate != null ? <>District repeater rate <b>{pct(rate, 2)}</b></> : "—";
  }
  if (metric === "critical_schools") {
    const count = data?.total_schools;
    const rate = districtPct ?? data?.district_pct;
    if (count != null && rate != null) {
      return (
        <>
          <b>{fmt(count)}</b> schools below 95% · district completion <b>{pct(rate, 2)}</b>
        </>
      );
    }
  }
  const rate = districtPct ?? data?.district_pct;
  return rate != null ? <>District completion <b>{pct(rate, 2)}</b></> : "—";
};

const renderMetricCell = (metric, school) => {
  if (metric === "non_certified" || metric === "certified_schools") {
    return school.certified || school.metric_pct || (metric === "certified_schools" ? "Yes" : "No");
  }
  if (metric === "pending_students") {
    return fmt(school.pending_students ?? school.metric_pct);
  }
  if (metric === "repeaters") {
    return fmt(school.repeaters ?? school.metric_pct);
  }
  if (metric === "high_repeaters") {
    return pct(school.repeater_rate ?? school.metric_pct, 2);
  }
  return pct(school.completion_pct ?? school.metric_pct, 2);
};

const showRepeatersColumn = (metric) => metric === "high_repeaters" || metric === "repeaters";

const showCertifiedFilter = (metric) => metric === "certified_schools" || metric === "non_certified";

const defaultCertifiedFilter = (metric) => (metric === "non_certified" ? "no" : metric === "certified_schools" ? "yes" : "");

const DataEntryKpiSchoolsModal = ({ open, metric, onClose, scopeLabel, districtPct, districtAggregate }) => {
  const navigate = useNavigate();
  const { scope } = useScope();
  const config = metric ? DATA_ENTRY_KPI_MODAL_CONFIG[metric] : null;
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState("");
  const [blockFilter, setBlockFilter] = useState("");
  const [schoolFilter, setSchoolFilter] = useState("");
  const [certifiedFilter, setCertifiedFilter] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (open && metric) {
      setCertifiedFilter(defaultCertifiedFilter(metric));
      setQuery("");
      setBlockFilter("");
      setSchoolFilter("");
      setPage(1);
    }
  }, [open, metric]);

  useEffect(() => {
    if (!open || !metric) return undefined;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);

    const params = { metric, limit: 5000 };
    if (scope.districtCode) params.district_code = scope.districtCode;
    if (scope.blockCode) params.block_code = scope.blockCode;
    if (scope.udiseCode) params.udise_code = scope.udiseCode;
    if (scope.districtName) params.district_name = scope.districtName;
    if (scope.blockName) params.block_name = scope.blockName;
    if (scope.schoolName) params.school_name = scope.schoolName;
    if (!scope.districtName && !scope.districtCode) params.district_name = "PUNE";

    const activeCertifiedFilter = showCertifiedFilter(metric)
      ? certifiedFilter || defaultCertifiedFilter(metric)
      : "";
    if (activeCertifiedFilter) {
      params.certification_filter = activeCertifiedFilter;
    }

    axios
      .get(`${API}/data-entry/kpi-schools`, { params })
      .then((res) => {
        if (!cancelled) setData(res.data || null);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.detail || "Failed to load school list");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    open,
    metric,
    scope.version,
    scope.districtCode,
    scope.blockCode,
    scope.udiseCode,
    scope.districtName,
    scope.blockName,
    scope.schoolName,
    certifiedFilter,
  ]);

  const blockOptions = useMemo(() => {
    const schools = data?.schools || [];
    return [...new Set(schools.map((s) => s.block_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [data?.schools]);

  const schoolOptions = useMemo(() => {
    const schools = data?.schools || [];
    const scoped = blockFilter
      ? schools.filter((s) => String(s.block_name || "") === blockFilter)
      : schools;
    return [...new Set(scoped.map((s) => s.school_name).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [data?.schools, blockFilter]);

  const filtered = useMemo(() => {
    let schools = data?.schools || [];
    if (blockFilter) {
      schools = schools.filter((s) => String(s.block_name || "") === blockFilter);
    }
    if (schoolFilter) {
      schools = schools.filter((s) => String(s.school_name || "") === schoolFilter);
    }
    const q = query.trim().toLowerCase();
    if (!q) return schools;
    return schools.filter(
      (s) =>
        String(s.school_name || "").toLowerCase().includes(q) ||
        String(s.block_name || "").toLowerCase().includes(q) ||
        String(s.certified || "").toLowerCase().includes(q) ||
        String(s.action_needed || "").toLowerCase().includes(q) ||
        String(s.udise_code || "").includes(q),
    );
  }, [data?.schools, blockFilter, schoolFilter, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const metricLabel = data?.metric_label || config?.label || "Value";
  const title = data?.title || config?.fallbackTitle || "Data Entry Schools";

  if (!open || !metric || !config) return null;

  return createPortal(
    <div className="usm-risk-modal-overlay" role="presentation" onClick={onClose}>
      <div
        className="usm-risk-modal usm-risk-modal-wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="data-entry-kpi-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="usm-risk-modal-head">
          <div>
            <p className="usm-risk-modal-eyebrow">Data entry drill-down · {scopeLabel || "District scope"}</p>
            <h3 id="data-entry-kpi-modal-title">{title}</h3>
            <p className="usm-risk-modal-sub">
              {formatCoverageSummary(metric, districtPct, districtAggregate, data)}
              {data?.description ? <> · {data.description}</> : null}
            </p>
          </div>
          <button type="button" className="usm-risk-modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="usm-risk-modal-toolbar usm-risk-modal-toolbar-filters">
          <label className="usm-risk-modal-filter">
            <span>Block</span>
            <select
              value={blockFilter}
              onChange={(e) => {
                setBlockFilter(e.target.value);
                setSchoolFilter("");
                setPage(1);
              }}
              disabled={loading || !blockOptions.length}
            >
              <option value="">All blocks</option>
              {blockOptions.map((block) => (
                <option key={block} value={block}>
                  {block}
                </option>
              ))}
            </select>
          </label>
          <label className="usm-risk-modal-filter">
            <span>School</span>
            <select
              value={schoolFilter}
              onChange={(e) => {
                setSchoolFilter(e.target.value);
                setPage(1);
              }}
              disabled={loading || !schoolOptions.length}
            >
              <option value="">All schools</option>
              {schoolOptions.map((school) => (
                <option key={school} value={school}>
                  {school}
                </option>
              ))}
            </select>
          </label>
          {showCertifiedFilter(metric) ? (
            <label className="usm-risk-modal-filter">
              <span>Certified</span>
              <select
                value={certifiedFilter}
                onChange={(e) => {
                  setCertifiedFilter(e.target.value);
                  setPage(1);
                }}
                disabled={loading}
              >
                <option value="all">All</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
          ) : null}
          {data ? (
            <span className="usm-risk-modal-count">
              Showing {fmt(filtered.length)} of {fmt(data.total_schools)} schools
            </span>
          ) : null}
        </div>

        <div className="usm-risk-modal-body">
          {loading ? (
            <div className="usm-risk-modal-loading">Loading schools…</div>
          ) : error ? (
            <div className="usm-risk-modal-error">{error}</div>
          ) : !data?.schools?.length ? (
            <div className="usm-risk-modal-empty">No schools match this gap in the current scope.</div>
          ) : (
            <>
              <div className="usm-risk-modal-body-search">
                <input
                  type="search"
                  className="usm-risk-modal-search"
                  placeholder="Search school, block, status, or UDISE…"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(1);
                  }}
                />
              </div>
              {!pageRows.length ? (
                <div className="usm-risk-modal-empty">
                  No schools match the selected block, school, certified status, or search.
                </div>
              ) : (
                <table className="usm-risk-modal-table usm-risk-modal-table-wide">
                  <thead>
                    <tr>
                      <th>Block</th>
                      <th>School</th>
                      <th>{metricLabel}</th>
                      {showRepeatersColumn(metric) ? <th>Repeaters</th> : null}
                      <th>Pending</th>
                      <th>Students</th>
                      <th>Priority</th>
                      <th>UDISE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((school) => (
                      <tr
                        key={school.udise_code || `${school.school_name}-${school.block_name}`}
                        className="usm-risk-modal-row"
                        onClick={() => school.udise_code && navigate(`/school/${school.udise_code}`)}
                        title={school.udise_code ? `${school.action_needed || ""} — Open school detail` : school.action_needed}
                      >
                        <td>{school.block_name || "—"}</td>
                        <td>{school.school_name || "—"}</td>
                        <td>{renderMetricCell(metric, school)}</td>
                        {showRepeatersColumn(metric) ? <td>{fmt(school.repeaters)}</td> : null}
                        <td>{fmt(school.pending_students)}</td>
                        <td>{fmt(school.total_students)}</td>
                        <td>
                          <span className={`usm-priority-pill ${String(school.priority || "").toLowerCase()}`}>
                            {school.priority || "—"}
                          </span>
                        </td>
                        <td>{school.udise_code || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </>
          )}
        </div>

        {!loading && !error && filtered.length > PAGE_SIZE ? (
          <div className="usm-risk-modal-footer">
            <button
              type="button"
              className="usm-risk-modal-page-btn"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              className="usm-risk-modal-page-btn"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
};

export default DataEntryKpiSchoolsModal;
